<template>
  <form @submit.prevent="submitForm" class="space-y-6">
    <!-- Title -->
    <div class="input-container">
      <label for="name" class="input-label">{{ t('slides_editor.components.lesson_settings_form.title') }}</label>
      <div class="input-wrapper">
        <input
          type="text"
          id="name"
          v-model="props.lessonSettings.name"
          :disabled="!props.lessonSettings.can_edit"
          :class="[
            'input-field',
            !props.lessonSettings.can_edit ? 'input-field--disabled' : ''
          ]"
          required
        />
      </div>
    </div>

    <!-- Description -->
    <div class="input-container">
      <label for="description" class="input-label">{{ t('slides_editor.components.lesson_settings_form.description') }}</label>
      <div class="input-wrapper">
        <textarea
          id="description"
          v-model="props.lessonSettings.description"
          :disabled="!props.lessonSettings.can_edit"
          :class="[
            'input-field input-textarea',
            !props.lessonSettings.can_edit ? 'input-field--disabled' : ''
          ]"
          required
        />
      </div>
      <p class="input-hint">{{ t('slides_editor.components.lesson_settings_form.description_hint') }}</p>
    </div>

    <!-- Category -->
    <div class="input-container">
      <label for="category" class="input-label">{{ t('slides_editor.components.lesson_settings_form.category') }}</label>
      <div class="input-wrapper">
        <select
          id="category"
          v-model="managedSelectedCategoryId"
          class="input-field input-select"
          required
        >
          <option value="" disabled>{{ t('slides_editor.components.lesson_settings_form.select') }}</option>
          <option v-for="category in categories" :key="category.id" :value="category.id">{{ category.title }}</option>
        </select>
      </div>
    </div>

    <!-- Year level -->
    <div class="input-container">
      <label class="input-label">{{ t('slides_editor.components.lesson_settings_form.year_level') }}</label>
      <PillInput
        :options="props.yearLevels"
        :selected-items="props.lessonSettings.year_levels || []"
        :use-id="true"
        prefix="year_level"
        :close-on-select="false"
        :autosubmit="false"
        :placeholder="t('slides_editor.components.lesson_settings_form.search_year_level')"
        @update:selectedItems="updateYearLevels"
      />
    </div>

    <!-- Cover photo -->
    <div class="input-container">
      <label class="input-label">{{ t('slides_editor.components.lesson_settings_form.cover_photo') }}</label>

      <!-- Current cover image preview -->
      <div v-if="props.lessonSettings.cover_file_name" class="mt-2 mb-2">
        <div class="w-full max-w-xs overflow-hidden mb-2">
          <img @click="openCoverPhotoModal" :src="currentCoverImageUrl" :alt="t('slides_editor.components.lesson_settings_form.current_cover_alt')" class="h-36 object-cover cursor-pointer"/>
        </div>

        <button
          type="button"
          @click="removeCoverImage"
          class="mt-1 w-full flex justify-center px-6 py-3 border border-gray-300 text-base font-medium rounded-xl text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          {{ t('slides_editor.components.lesson_settings_form.delete_cover_photo') }}
        </button>
      </div>

      <button
        v-else
        type="button"
        @click="openCoverPhotoModal"
        class="mt-1 w-full flex justify-center px-6 py-3 border border-gray-300 text-base font-medium rounded-xl text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
      >
        {{ t('slides_editor.components.lesson_settings_form.select_cover_photo') }}
      </button>
    </div>

    <!-- Device free mode -->
    <div v-if="!school?.device_free" class="input-container">
      <h4 class="input-label">{{ t('slides_editor.components.lesson_settings_form.device_free_mode') }}</h4>
      <div class="input-checkbox-wrapper">
        <div class="flex h-5 items-center">
          <input
            id="deviceFree"
            type="checkbox"
            v-model="props.lessonSettings.device_free_compatible"
            class="input-checkbox"
          />
        </div>
        <div class="ml-3 text-body-sm">
          <label for="deviceFree" class="input-checkbox-label">{{ t('slides_editor.components.lesson_settings_form.device_free_compatible') }}</label>
          <p class="input-checkbox-description">{{ t('slides_editor.components.lesson_settings_form.device_free_description') }}</p>
        </div>
      </div>
    </div>
  </form>

  <!-- Delete lesson -->
  <div class="mt-2" v-if="props.lessonSettings.can_destroy">
    <turbo-frame id="delete-lesson-button" :src="`/schools/${school.id}/lessons/${props.lessonSettings.id}/editor/destroy`">
    </turbo-frame>
  </div>

  <div v-if="error" class="text-red-600 text-sm mt-2">
    {{ error }}
  </div>

  <CoverSelectionModal
    v-model="showCoverPhotoModal"
    :covers="props.lessonCovers"
    :initial-selected-cover="props.lessonSettings.cover_file_name"
    @select-cover="handleCoverSelection"
  />
</template>

<script setup>
import { ref, watch, computed, inject } from 'vue'
import debounce from 'debounce'
import PillInput from '../shared/PillInput.vue'
import CoverSelectionModal from './CoverSelectionModal.vue'
import { useLessonUpdate } from '../../composables/useLessonUpdate'
import { t } from '@/libs/i18n'

const props = defineProps({
  categories: {
    type: Array,
    required: true,
    default: () => []
  },
  yearLevels: {
    type: Array,
    required: true,
    default: () => []
  },
  lessonSettings: {
    type: Object,
    required: true
  },
  updatePath: {
    type: String,
    required: true
  },
  lessonCovers: {
    type: Array,
    required: true,
    default: () => []
  }
})

const school = inject('school')

const managedSelectedCategoryId = computed({
  get: () => {
    const categories = props.lessonSettings.categories || []
    return categories.length > 0 ? categories[0] : ''
  },
  set: (newId) => {
    props.lessonSettings.categories = newId ? [newId] : []
  }
})

const updateYearLevels = (newYearLevels) => {
  if (!props.lessonSettings.hasOwnProperty('year_levels') && newYearLevels) {
    props.lessonSettings.year_levels = []
  }
  props.lessonSettings.year_levels = newYearLevels
}

const { updateLessonSettings, error } = useLessonUpdate()

const submitForm = () => {
  const yearLevelsPayload = (props.lessonSettings.year_levels || []).map(pair => pair[1])

  const payload = {
    lesson_version: {
      lesson_attributes: {
        id: props.lessonSettings.id,
        name: props.lessonSettings.name,
        description: props.lessonSettings.description
      },
      categories: props.lessonSettings.categories || [],
      year_levels: yearLevelsPayload,
      device_free_compatible: props.lessonSettings.device_free_compatible || false,
      cover_file_name: props.lessonSettings.cover_file_name || null
    }
  }

  if (props.lessonSettings.can_edit) {
    payload.lesson_version.version_description = props.lessonSettings.description
  }

  updateLessonSettings(props.updatePath, JSON.stringify(payload))
}

const debouncedUpdate = debounce(submitForm, 1000)

watch(
  () => props.lessonSettings,
  () => {
    if (props.lessonSettings && typeof props.lessonSettings.categories === 'undefined') {
      props.lessonSettings.categories = []
    }
    if (props.lessonSettings && typeof props.lessonSettings.year_levels === 'undefined') {
      props.lessonSettings.year_levels = []
    }
    debouncedUpdate()
  },
  { deep: true }
)

const showCoverPhotoModal = ref(false)

const openCoverPhotoModal = () => {
  showCoverPhotoModal.value = true
}

const handleCoverSelection = (fileName) => {
  props.lessonSettings.cover_file_name = fileName
}

const removeCoverImage = () => {
  props.lessonSettings.cover_file_name = null
}

const currentCoverImageUrl = computed(() => {
  if (!props.lessonSettings.cover_file_name) return null

  const cover = props.lessonCovers.find(c => {
    return props.lessonSettings.cover_file_name === c.file_name
  })

  if (cover) return cover.url

  if (props.lessonSettings.cover_url) return props.lessonSettings.cover_url

  return null
})

</script>
